import { Trash2, Plus, Info, ChevronRight } from "@semo/icons";
import {
  useExternalEmbedControllerGetEmbedUrl,
  type QuoteItemDto,
} from "@semo/api-contracts";
import { combineSpecs } from "@semo/shared-utils";
import { Input, Button } from "@semo/shared-ui";
import {
  hasStoredEmbedSession,
  extractEmbedTokenFromLocation,
} from "../../features/auth";
import { ItemNameAutocomplete } from "./ItemNameAutocomplete";

interface QuoteRequestItemDtosTableProps {
  items: QuoteItemDto[];
  showSpec?: boolean;
  isProjectBased?: boolean;
  isEditable?: boolean;
  readOnly?: boolean; // 상세 보기용 완전 읽기 전용
  columnLabel?: "금액" | "계"; // 컬럼 라벨 통일 (기본값: '금액')
  /** 단가 열 제목 — 입력 축을 그대로 적는다(기본: VAT 별도) */
  unitPriceLabel?: string;
  // 단가 열 헤더 아래에 붙는 부가세 축 토글 (편집 모드에서만 표시).
  // vatMode + onVatModeChange 둘 다 있을 때만 렌더링 — read-only/detail 뷰는 무영향.
  vatMode?: "EXCLUSIVE" | "INCLUSIVE";
  onVatModeChange?: (mode: "EXCLUSIVE" | "INCLUSIVE") => void;
  enableAutocomplete?: boolean; // 자동완성 활성화 여부
  supplierId?: string; // 공급사 ID (자동완성 사용 시 필수)
  supplierPriceMap?: Map<string, { supplierPrice: number | null }>; // ✨ 카탈로그 단가 정보
  // 'specification' 필드 추가: 단일 규격 필드로 통합 (spec1에 저장)
  onItemChange?: (
    index: number,
    field: keyof QuoteItemDto | "specification",
    value: string,
  ) => void;
  onPriceChange?: (index: number, value: string) => void;
  onAddItem?: () => void;
  onDeleteItem?: (index: number) => void;
  onAutocompleteSelect?: (
    index: number,
    itemName: string,
    unitPrice: number,
    itemId: string,
    spec1?: string | null,
    spec2?: string | null,
    spec3?: string | null,
  ) => void;
}

/**
 * 견적 항목 테이블 공통 컴포넌트
 * ResponseQuoteModal과 EditQuoteModal에서 사용
 */
export function QuoteRequestItemDtosTable({
  items,
  showSpec = false,
  isProjectBased = false,
  isEditable = false,
  readOnly = false,
  columnLabel = "금액",
  unitPriceLabel = "단가(VAT 별도)",
  vatMode,
  onVatModeChange,
  enableAutocomplete = false,
  supplierId,
  supplierPriceMap,
  onItemChange,
  onPriceChange,
  onAddItem,
  onDeleteItem,
  onAutocompleteSelect,
}: QuoteRequestItemDtosTableProps) {
  const showVatToggle = isEditable && !!vatMode && !!onVatModeChange;
  const isEmbedContext =
    hasStoredEmbedSession() || extractEmbedTokenFromLocation() !== null;
  const embedUrlMutation = useExternalEmbedControllerGetEmbedUrl();

  // In the embed context this component runs inside a cross-origin C-Market
  // iframe whose sessionStorage is partitioned by top-level site: a tab
  // opened via window.open does NOT inherit the session, and the original
  // OTT is single-use (already burned). So the deep link mints a fresh
  // single-use embed URL (bearer = current embed access token) for the new
  // tab instead of re-appending any token.
  const openInventory = async (): Promise<void> => {
    if (!isEmbedContext) {
      window.open("/?tab=products", "_blank");
      return;
    }

    // Open the tab synchronously (inside the user gesture, so popup blockers
    // allow it), then navigate it once the fresh handoff URL arrives.
    const inventoryTab = window.open("", "_blank");
    try {
      const response = await embedUrlMutation.mutateAsync({
        data: { type: "dashboard-products" },
      });
      const embedUrl = response.data?.embedUrl;
      if (embedUrl && inventoryTab) {
        inventoryTab.location.href = embedUrl;
      } else {
        inventoryTab?.close();
      }
    } catch {
      inventoryTab?.close();
    }
  };

  return (
    <div className="mb-6 sm:mb-8">
      {/* ✨ 단가 자동 완성 가이드 (업체가 견적 작성/수정 중일 때 상시 노출) */}
      {isEditable && supplierId && (
        <div className="mb-4 p-3 sm:px-4 sm:py-2.5 bg-primary/5 border border-primary/15 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-sm animate-in fade-in slide-in-from-top-1 duration-300">
          <div className="flex items-start gap-2.5">
            <Info className="w-4 h-4 text-primary-text shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs sm:text-sm text-primary-pressed font-semibold leading-snug">
                등록된 품목 단가나 최근 내역으로{" "}
                <span className="underline decoration-primary/25 underline-offset-2">
                  단가가 자동 완성
                </span>
                됩니다.
              </p>
              <p className="mt-0.5 text-primary-text/80 font-normal text-2xs sm:text-xs">
                자주 쓰는 단가는 물품 관리에서 미리 설정해 보세요.
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void openInventory()}
            disabled={embedUrlMutation.isPending}
            className="h-8 px-2.5 text-primary-text hover:text-primary-hover hover:bg-primary/10 transition-all font-semibold gap-1 text-2xs sm:text-sm shrink-0 w-fit self-end sm:self-center -mr-1 sm:mr-0"
          >
            물품 관리 바로가기 <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}

      {/* Desktop Table View (Hidden on Mobile) */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full border-separate border-spacing-0 min-w-[700px]">
          <thead>
            <tr>
              <th className="bg-[#e9f4ff] text-gray-800 py-2 sm:py-3 px-1 text-center font-semibold text-xs sm:text-sm border-t border-b border-[#dbe8f7] w-10">
                No
              </th>
              <th className="bg-[#e9f4ff] text-gray-800 py-2 sm:py-3 px-2 text-center font-semibold text-xs sm:text-sm border-t border-b border-[#dbe8f7]">
                {isProjectBased
                  ? "프로젝트명"
                  : showSpec
                    ? "품명 및 규격"
                    : "품명"}
              </th>
              {!isProjectBased && (
                <>
                  <th className="bg-[#e9f4ff] text-gray-800 py-2 sm:py-3 px-1 text-center font-semibold text-xs sm:text-sm border-t border-b border-[#dbe8f7] w-16">
                    수량
                  </th>
                  <th className="bg-[#e9f4ff] text-gray-800 py-2 sm:py-3 px-1 text-center font-semibold text-xs sm:text-sm border-t border-b border-[#dbe8f7] w-16">
                    단위
                  </th>
                </>
              )}
              {isProjectBased && (
                <>
                  <th className="bg-[#e9f4ff] text-gray-800 py-2 sm:py-3 px-1 text-center font-semibold text-xs sm:text-sm border-t border-b border-[#dbe8f7] w-16">
                    수량
                  </th>
                  <th className="bg-[#e9f4ff] text-gray-800 py-2 sm:py-3 px-1 text-center font-semibold text-xs sm:text-sm border-t border-b border-[#dbe8f7] w-16">
                    단위
                  </th>
                </>
              )}
              <th className="bg-[#e9f4ff] text-gray-800 py-2 sm:py-3 px-2 text-center font-semibold text-xs sm:text-sm border-t border-b border-[#dbe8f7] w-44">
                <div className="flex flex-col items-center gap-1.5">
                  <span>
                    {unitPriceLabel} {!isEditable && "(입력)"}
                  </span>
                  {showVatToggle && (
                    <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5">
                      {(
                        [
                          { value: "EXCLUSIVE", label: "VAT 별도" },
                          { value: "INCLUSIVE", label: "VAT 포함" },
                        ] as const
                      ).map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => onVatModeChange?.(option.value)}
                          aria-pressed={vatMode === option.value}
                          className={`whitespace-nowrap rounded px-1.5 py-0.5 text-2xs sm:text-2xs font-semibold transition-colors ${
                            vatMode === option.value
                              ? "bg-primary/5 text-primary-text"
                              : "text-gray-500 hover:text-gray-700"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </th>
              <th className="bg-[#e9f4ff] text-gray-800 py-2 sm:py-3 px-2 text-center font-semibold text-xs sm:text-sm border-t border-b border-[#dbe8f7] w-28">
                {columnLabel}
              </th>
              {isEditable && (
                <th className="bg-[#e9f4ff] text-gray-800 py-2 sm:py-3 px-1 text-center font-semibold text-xs sm:text-sm border-t border-b border-[#dbe8f7] w-10"></th>
              )}
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr
                key={index}
                className="group hover:bg-primary/5 transition-colors"
              >
                <td className="py-2 px-1 text-center text-sm text-gray-600 border-b border-[#eef2f7]">
                  {index + 1}
                </td>
                <td className="py-2 px-2 text-left border-b border-[#eef2f7]">
                  {readOnly ? (
                    <>
                      <div className="text-sm font-semibold text-gray-900 px-2 py-1 mb-1.5">
                        {item.name}
                      </div>
                      {showSpec &&
                        !isProjectBased &&
                        combineSpecs(item) !== "-" && (
                          <div className="text-xs text-gray-600 font-medium px-2 py-1">
                            {combineSpecs(item)}
                          </div>
                        )}
                    </>
                  ) : isEditable ? (
                    <div className="flex flex-col gap-1.5">
                      {enableAutocomplete && supplierId ? (
                        <ItemNameAutocomplete
                          value={item.name}
                          supplierId={supplierId}
                          onSelect={(
                            itemName,
                            unitPrice,
                            itemId,
                            spec1,
                            spec2,
                            spec3,
                          ) => {
                            onAutocompleteSelect?.(
                              index,
                              itemName,
                              unitPrice,
                              itemId,
                              spec1,
                              spec2,
                              spec3,
                            );
                          }}
                          onChange={(value) =>
                            onItemChange?.(index, "name", value)
                          }
                          inputSize="sm"
                          placeholder="품명 입력 (2글자 이상 입력 시 자동완성)"
                        />
                      ) : (
                        <Input
                          type="text"
                          value={item.name}
                          onChange={(e) =>
                            onItemChange?.(index, "name", e.target.value)
                          }
                          className="w-full text-sm font-semibold text-gray-900"
                          inputSize="sm"
                          placeholder="품명 입력"
                        />
                      )}
                      {showSpec && (
                        <Input
                          type="text"
                          value={combineSpecs(item)}
                          onChange={(e) =>
                            onItemChange?.(
                              index,
                              "specification",
                              e.target.value,
                            )
                          }
                          className="w-full text-xs text-gray-600 font-medium"
                          inputSize="sm"
                          placeholder="규격/사양 (예: 15.6인치, i5, 8GB RAM)"
                        />
                      )}
                    </div>
                  ) : (
                    <>
                      <Input
                        type="text"
                        value={item.name}
                        disabled
                        className="w-full text-sm font-semibold text-gray-500 bg-gray-50/50 mb-1.5 border-transparent"
                        inputSize="sm"
                      />
                      {showSpec &&
                        !isProjectBased &&
                        combineSpecs(item) !== "-" && (
                          <Input
                            type="text"
                            value={combineSpecs(item)}
                            disabled
                            className="w-full text-xs text-gray-400 font-medium bg-gray-50/50 border-transparent"
                            inputSize="sm"
                          />
                        )}
                    </>
                  )}
                </td>
                <td className="py-2 px-1 text-center border-b border-[#eef2f7]">
                  {readOnly ? (
                    <span className="text-sm text-gray-900">
                      {item.quantity.toLocaleString()}
                    </span>
                  ) : isEditable ? (
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={item.quantity.toLocaleString()}
                      onChange={(e) =>
                        onItemChange?.(index, "quantity", e.target.value)
                      }
                      className="w-full text-center text-sm text-gray-900"
                      inputSize="sm"
                    />
                  ) : (
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={item.quantity.toLocaleString()}
                      disabled
                      className="w-full text-center text-sm text-gray-500 bg-gray-50/50 border-transparent"
                      inputSize="sm"
                    />
                  )}
                </td>
                <td className="py-2 px-1 text-center border-b border-[#eef2f7]">
                  {readOnly ? (
                    <span className="text-sm text-gray-600">{item.unit}</span>
                  ) : isEditable ? (
                    <Input
                      type="text"
                      value={item.unit}
                      onChange={(e) =>
                        onItemChange?.(index, "unit", e.target.value)
                      }
                      className="w-full text-center text-sm text-gray-600"
                      inputSize="sm"
                    />
                  ) : (
                    <Input
                      type="text"
                      value={item.unit}
                      disabled
                      className="w-full text-center text-sm text-gray-500 bg-gray-50/50 border-transparent"
                      inputSize="sm"
                    />
                  )}
                </td>
                <td className="py-2 px-2 text-right border-b border-[#eef2f7]">
                  {readOnly ? (
                    <span className="text-sm text-gray-900 font-medium">
                      {(item.unitPrice || 0).toLocaleString("ko-KR")}
                    </span>
                  ) : isEditable ? (
                    <div className="flex flex-col items-end gap-1">
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={(item.unitPrice || 0).toLocaleString("ko-KR")}
                        onChange={(e) => onPriceChange?.(index, e.target.value)}
                        className={`w-full text-right text-sm font-medium ${
                          item.itemId &&
                          supplierPriceMap?.get(item.itemId)?.supplierPrice &&
                          supplierPriceMap.get(item.itemId)?.supplierPrice !==
                            item.unitPrice
                            ? "border-primary/50 ring-1 ring-primary/15"
                            : ""
                        }`}
                        inputSize="sm"
                      />
                      {item.itemId &&
                        supplierPriceMap?.get(item.itemId)?.supplierPrice &&
                        supplierPriceMap.get(item.itemId)?.supplierPrice !==
                          item.unitPrice && (
                          <div className="text-2xs text-primary-text font-semibold whitespace-nowrap bg-primary/5 px-1 rounded">
                            카탈로그:{" "}
                            {supplierPriceMap
                              .get(item.itemId)
                              ?.supplierPrice?.toLocaleString()}
                            원
                          </div>
                        )}
                    </div>
                  ) : (
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={(item.unitPrice || 0).toLocaleString("ko-KR")}
                      onChange={(e) => onPriceChange?.(index, e.target.value)}
                      className="w-full text-right text-sm text-gray-900 border-primary/40 font-semibold"
                      inputSize="sm"
                      placeholder="0"
                    />
                  )}
                </td>
                <td className="py-2 px-2 text-right text-sm text-gray-500 border-b border-[#eef2f7] font-medium bg-gray-100/50 rounded-sm">
                  {((item.unitPrice || 0) * item.quantity).toLocaleString(
                    "ko-KR",
                  )}
                </td>
                {isEditable && (
                  <td className="py-2 px-1 text-center border-b border-[#eef2f7]">
                    <button
                      onClick={() => onDeleteItem?.(index)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all opacity-100"
                      title="행 삭제"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                )}
              </tr>
            ))}

            {isEditable && (
              <tr>
                <td colSpan={7} className="py-2 px-2 border-b border-[#eef2f7]">
                  <button
                    onClick={onAddItem}
                    className="w-full py-2 border-2 border-dashed border-gray-200 rounded-lg text-gray-400 hover:border-primary/60 hover:text-primary-text hover:bg-primary/5 transition-all flex items-center justify-center gap-2 text-sm font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    품목 추가하기
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View (Visible on Mobile) */}
      <div className="block md:hidden space-y-4">
        {items.map((item, index) => (
          <div
            key={index}
            className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
          >
            <div className="flex justify-between items-start mb-3">
              <span className="text-xs font-semibold text-primary-text bg-primary/5 px-2 py-1 rounded">
                No. {index + 1}
              </span>
              {isEditable && (
                <button
                  onClick={() => onDeleteItem?.(index)}
                  className="text-gray-400 hover:text-red-600 p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Item Name */}
            <div className="mb-3">
              <div className="block text-xs font-medium text-gray-500 mb-1">
                {isProjectBased
                  ? "프로젝트명"
                  : showSpec
                    ? "품명 및 규격"
                    : "품명"}
              </div>
              {readOnly ? (
                <div className="text-sm font-semibold text-gray-900">
                  {item.name}
                  {showSpec &&
                    !isProjectBased &&
                    combineSpecs(item) !== "-" && (
                      <div className="text-xs text-gray-500 mt-1">
                        {combineSpecs(item)}
                      </div>
                    )}
                </div>
              ) : isEditable ? (
                <div className="flex flex-col gap-2">
                  {enableAutocomplete && supplierId ? (
                    <ItemNameAutocomplete
                      value={item.name}
                      supplierId={supplierId}
                      onSelect={(
                        itemName,
                        unitPrice,
                        itemId,
                        spec1,
                        spec2,
                        spec3,
                      ) => {
                        onAutocompleteSelect?.(
                          index,
                          itemName,
                          unitPrice,
                          itemId,
                          spec1,
                          spec2,
                          spec3,
                        );
                      }}
                      onChange={(value) => onItemChange?.(index, "name", value)}
                      placeholder="품명 입력"
                      className="w-full"
                      inputSize="default"
                    />
                  ) : (
                    <Input
                      type="text"
                      value={item.name}
                      onChange={(e) =>
                        onItemChange?.(index, "name", e.target.value)
                      }
                      className="w-full text-sm font-semibold text-gray-900"
                      placeholder="품명 입력"
                      inputSize="default"
                    />
                  )}
                  {showSpec && (
                    <Input
                      type="text"
                      value={combineSpecs(item)}
                      onChange={(e) =>
                        onItemChange?.(index, "specification", e.target.value)
                      }
                      className="w-full text-xs text-gray-600"
                      placeholder="규격/사양"
                      inputSize="default"
                    />
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                  {item.name}
                </div>
              )}
            </div>

            {/* Quantity & Unit */}
            {!isProjectBased && (
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <div className="block text-xs font-medium text-gray-500 mb-1">
                    수량
                  </div>
                  {readOnly ? (
                    <div className="text-sm text-gray-900">
                      {item.quantity.toLocaleString()}
                    </div>
                  ) : isEditable ? (
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={item.quantity.toLocaleString()}
                      onChange={(e) =>
                        onItemChange?.(index, "quantity", e.target.value)
                      }
                      className="w-full text-center text-sm text-gray-900"
                      inputSize="default"
                    />
                  ) : (
                    <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded text-center">
                      {item.quantity.toLocaleString()}
                    </div>
                  )}
                </div>
                <div>
                  <div className="block text-xs font-medium text-gray-500 mb-1">
                    단위
                  </div>
                  {readOnly ? (
                    <div className="text-sm text-gray-900">{item.unit}</div>
                  ) : isEditable ? (
                    <Input
                      type="text"
                      value={item.unit}
                      onChange={(e) =>
                        onItemChange?.(index, "unit", e.target.value)
                      }
                      className="w-full text-center text-sm text-gray-900"
                      inputSize="default"
                    />
                  ) : (
                    <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded text-center">
                      {item.unit}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Unit Price */}
            <div className="mb-3">
              <div className="block text-xs font-medium text-gray-500 mb-1">
                {unitPriceLabel}
              </div>
              {readOnly ? (
                <div className="text-sm font-semibold text-gray-900 text-right">
                  {(item.unitPrice || 0).toLocaleString("ko-KR")}
                </div>
              ) : isEditable ? (
                <div className="relative">
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={(item.unitPrice || 0).toLocaleString("ko-KR")}
                    onChange={(e) => onPriceChange?.(index, e.target.value)}
                    className={`w-full text-right text-base font-semibold ${
                      item.itemId &&
                      supplierPriceMap?.get(item.itemId)?.supplierPrice &&
                      supplierPriceMap.get(item.itemId)?.supplierPrice !==
                        item.unitPrice
                        ? "border-primary/50 ring-1 ring-primary/15"
                        : ""
                    }`}
                    inputSize="default"
                  />
                  {item.itemId &&
                    supplierPriceMap?.get(item.itemId)?.supplierPrice &&
                    supplierPriceMap.get(item.itemId)?.supplierPrice !==
                      item.unitPrice && (
                      <div className="absolute right-0 -bottom-5 text-2xs text-primary-text font-semibold bg-primary/5 px-1 rounded">
                        카탈로그:{" "}
                        {supplierPriceMap
                          .get(item.itemId)
                          ?.supplierPrice?.toLocaleString()}
                        원
                      </div>
                    )}
                </div>
              ) : (
                <div className="text-right text-base font-semibold text-gray-900 bg-gray-50 p-2 rounded">
                  {(item.unitPrice || 0).toLocaleString("ko-KR")}
                </div>
              )}
            </div>

            {/* Total Amount */}
            <div className="flex justify-between items-center pt-3 border-t border-gray-100 bg-gray-50/50 -mx-4 px-4 -mb-4 py-3 rounded-b-xl">
              <span className="text-xs font-semibold text-gray-600">
                {columnLabel}
              </span>
              <span className="text-base font-semibold text-gray-900">
                {((item.unitPrice || 0) * item.quantity).toLocaleString(
                  "ko-KR",
                )}
                원
              </span>
            </div>
          </div>
        ))}

        {isEditable && (
          <button
            onClick={onAddItem}
            className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-primary/60 hover:text-primary-text hover:bg-primary/5 transition-all flex items-center justify-center gap-2 text-sm font-semibold"
          >
            <Plus className="w-5 h-5" />
            품목 추가하기
          </button>
        )}
      </div>
    </div>
  );
}
